import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useDeviceAlerts } from './useDeviceAlerts.js'
import { useUserDevices } from './useUserDevices.js'

/**
 * Firebase v11+ Device Health Monitor Hook
 * 
 * React 19+ optimized automated monitoring and alerting system.
 * Continuously monitors device connectivity, performance, and health status
 * with minimal re-renders and efficient state management.
 * 
 * Features:
 * - Memoized monitoring thresholds for performance
 * - Debounced alert creation to prevent spam
 * - Efficient timer management with automatic cleanup
 * - Real-time device status integration
 * 
 * @param {boolean} globalPollingPaused - Whether global polling is paused
 * @returns {Object} Monitoring state and metrics
 */
export function useDeviceHealthMonitor(globalPollingPaused = false) {
  const { devices } = useUserDevices()
  const { createConnectivityAlert, createPerformanceAlert, createHealthAlert } = useDeviceAlerts()
  
  // Efficient state tracking with refs to prevent re-renders
  const previousStates = useRef(new Map())
  const alertTimers = useRef(new Map())
  
  // Memoized monitoring configuration for performance
  const monitoringConfig = useMemo(() => ({
    connectivity: {
      offlineAlertDelay: 5 * 60 * 1000, // 5 minutes
      extendedOfflineThreshold: 24, // hours
    },
    performance: {
      cpuThreshold: 85,
      memoryThreshold: 90,
      cpuAlertDelay: 2 * 60 * 1000, // 2 minutes
    },
    polling: {
      interval: 30 * 1000, // 30 seconds
    },
  }), [])

  // Optimized connectivity monitoring with debounced alerts
  const monitorConnectivity = useCallback((deviceId, current, previous) => {
    const { connectionStatus, lastSeen } = current
    const wasOnline = previous.connectionStatus === 'online'
    const isOffline = connectionStatus === 'offline'
    
    // Device went offline - set delayed alert
    if (wasOnline && isOffline) {
      const timerKey = `offline-${deviceId}`
      
      if (!alertTimers.current.has(timerKey)) {
        const timer = setTimeout(() => {
          createConnectivityAlert(deviceId, connectionStatus, lastSeen)
          alertTimers.current.delete(timerKey)
        }, monitoringConfig.connectivity.offlineAlertDelay)
        
        alertTimers.current.set(timerKey, timer)
      }
    }
    
    // Device came online - cancel pending offline alert
    if (!wasOnline && connectionStatus === 'online') {
      const timerKey = `offline-${deviceId}`
      const timer = alertTimers.current.get(timerKey)
      if (timer) {
        clearTimeout(timer)
        alertTimers.current.delete(timerKey)
      }
    }

    // Extended offline monitoring with efficient time calculation
    if (isOffline && lastSeen) {
      const hoursOffline = (Date.now() - new Date(lastSeen).getTime()) / (1000 * 60 * 60)
      
      if (hoursOffline >= monitoringConfig.connectivity.extendedOfflineThreshold && 
          hoursOffline % 24 < 1) {
        createConnectivityAlert(deviceId, connectionStatus, lastSeen)
      }
    }
  }, [createConnectivityAlert, monitoringConfig.connectivity])

  // Optimized performance monitoring with configurable thresholds
  const monitorPerformance = useCallback((deviceId, current, previous) => {
    const { performance } = current
    if (!performance) return

    const { cpuThreshold, memoryThreshold, cpuAlertDelay } = monitoringConfig.performance

    // CPU monitoring with debounced alerts
    if (performance.cpuUsage !== undefined) {
      const previousCpu = previous.performance?.cpuUsage
      
      if (performance.cpuUsage > cpuThreshold && 
          (previousCpu === undefined || previousCpu <= cpuThreshold)) {
        
        const timerKey = `cpu-${deviceId}`
        if (!alertTimers.current.has(timerKey)) {
          const timer = setTimeout(() => {
            createPerformanceAlert(deviceId, 'cpu', performance.cpuUsage, cpuThreshold)
            alertTimers.current.delete(timerKey)
          }, cpuAlertDelay)
          
          alertTimers.current.set(timerKey, timer)
        }
      } else if (performance.cpuUsage <= cpuThreshold) {
        // Clear pending CPU alert
        const timerKey = `cpu-${deviceId}`
        const timer = alertTimers.current.get(timerKey)
        if (timer) {
          clearTimeout(timer)
          alertTimers.current.delete(timerKey)
        }
      }
    }

    // Memory monitoring with immediate alerts for critical issues
    if (performance.memoryUsage !== undefined) {
      const previousMemory = previous.performance?.memoryUsage
      
      if (performance.memoryUsage > memoryThreshold && 
          (previousMemory === undefined || previousMemory <= memoryThreshold)) {
        createPerformanceAlert(deviceId, 'memory', performance.memoryUsage, memoryThreshold)
      }
    }
  }, [createPerformanceAlert, monitoringConfig.performance])

  // Efficient health check monitoring with status change detection
  const monitorHealthChecks = useCallback((deviceId, current, previous) => {
    const { healthChecks, healthStatus } = current
    if (!healthChecks) return

    const previousHealth = previous.healthStatus
    const currentHealth = healthStatus
    
    // Alert on health deterioration
    if (previousHealth === 'healthy' && 
        (currentHealth === 'warning' || currentHealth === 'critical')) {
      createHealthAlert(deviceId, healthChecks)
    }

    // Detect new health check failures with efficient filtering
    const previousChecks = previous.healthChecks || {}
    const hasNewFailures = Object.entries(healthChecks).some(
      ([check, status]) => !status && previousChecks[check] === true,
    )
    
    if (hasNewFailures) {
      createHealthAlert(deviceId, healthChecks)
    }
  }, [createHealthAlert])

  // Memoized device count to prevent unnecessary re-renders
  const monitoredDevicesCount = useMemo(() => devices?.length || 0, [devices?.length])
  
  // Main monitoring effect with optimized device processing
  useEffect(() => {
    if (globalPollingPaused) return
    if (!monitoredDevicesCount) return

    const monitorDevices = () => {
      devices.forEach(device => {
        const deviceKey = device.deviceId
        const previousState = previousStates.current.get(deviceKey) || {}
        
        // Extract current device state from real device data
        // This integrates with the actual device status from useUserDevices
        const currentState = {
          connectionStatus: device.connectionStatus || 'unknown',
          healthStatus: device.healthStatus || 'unknown',
          lastSeen: device.lastSeen || device.lastHeartbeat || new Date(),
          performance: device.performance || device.stats || {},
          healthChecks: device.healthChecks || {},
        }

        // Run optimized monitoring checks
        monitorConnectivity(device.deviceId, currentState, previousState)
        monitorPerformance(device.deviceId, currentState, previousState)
        monitorHealthChecks(device.deviceId, currentState, previousState)

        // Update state efficiently
        previousStates.current.set(deviceKey, currentState)
      })
    }

    // Initial monitoring run
    monitorDevices()

    // Set up periodic monitoring with configurable interval
    const monitoringInterval = setInterval(monitorDevices, monitoringConfig.polling.interval)

    return () => clearInterval(monitoringInterval)
  }, [
    globalPollingPaused,
    monitoredDevicesCount, 
    devices, 
    monitorConnectivity, 
    monitorPerformance, 
    monitorHealthChecks,
    monitoringConfig.polling.interval,
  ])

  // Cleanup effect for timer management
  useEffect(() => () => {
    // Clear all pending timers on unmount to prevent memory leaks
    alertTimers.current.forEach(timer => clearTimeout(timer))
    alertTimers.current.clear()
  }, [])

  // Return memoized monitoring state
  return useMemo(() => ({
    monitoredDevicesCount,
    isMonitoring: monitoredDevicesCount > 0,
  }), [monitoredDevicesCount])
}
